import React, { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { driverApi } from '../../config/driverApi';
import { 
  ClipboardList, 
  Truck, 
  CheckCircle, 
  XCircle, 
  DollarSign, 
  Star,
  Navigation,
  Clock
} from 'lucide-react';

const DriverDashboard = () => {
  const { driver, setDriver } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await driverApi.get('/dashboard.php?action=dashboard');
        if (res.data.success) {
          setStats(res.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#999' }}>
        <div style={{
          width: '36px',
          height: '36px',
          border: '3px solid #f0f0f0',
          borderTopColor: '#D4AF37',
          borderRadius: '50%',
          margin: '0 auto 16px',
          animation: 'spin 0.8s linear infinite',
        }}></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#999' }}>
        <p>Failed to load dashboard</p>
      </div>
    );
  }

  const statCards = [
    { label: 'Assigned Orders', value: stats.assigned_orders, icon: ClipboardList, color: '#FFC107' },
    { label: 'Pending Pickups', value: stats.pending_pickups, icon: Truck, color: '#FF9800' },
    { label: 'In Transit', value: stats.in_transit, icon: Navigation, color: '#00BCD4' },
    { label: 'Completed Today', value: stats.completed_today, icon: CheckCircle, color: '#4CAF50' },
    { label: 'Earnings Today', value: `UGX ${stats.earnings_today.toLocaleString()}`, icon: DollarSign, color: '#4CAF50' },
    { label: 'Total Deliveries', value: stats.total_deliveries, icon: CheckCircle, color: '#2196F3' },
  ];

  const handleStatusChange = async (newStatus) => {
    setStatusUpdating(true);
    try {
      const res = await driverApi.post('/auth.php?action=availability', { availability: newStatus });
      if (res.data.success) {
        setDriver(prev => ({ ...prev, availability: newStatus }));
      } else {
        alert(res.data.message || 'Failed to update status');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Network error');
    } finally {
      setStatusUpdating(false);
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'online': return { label: 'Online', color: '#4CAF50' };
      case 'busy': return { label: 'Busy', color: '#FF9800' };
      default: return { label: 'Offline', color: '#757575' };
    }
  };

  const statusConfig = getStatusConfig(driver?.availability);

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#1A1A1A',
            marginBottom: '4px',
          }}>
            Dashboard
          </h2>
          <p style={{ color: '#666', fontSize: '0.9rem', margin: 0 }}>
            Welcome back, {driver?.name}! Here's your delivery summary.
          </p>
        </div>
        
        {/* Availability Badge */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          backgroundColor: '#fff',
          borderRadius: '20px',
          border: '1px solid #EAEDF1',
          boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
        }}>
          <span style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            backgroundColor: statusConfig.color,
            display: 'inline-block',
            boxShadow: `0 0 8px ${statusConfig.color}`,
          }} />
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>
            Status: {statusConfig.label}
          </span>
        </div>
      </div>

      {/* Availability Selector Card */}
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '24px',
        border: '1px solid #EAEDF1',
        boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
      }}>
        <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: '#333', margin: '0 0 12px' }}>
          Set Your Availability
        </h3>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {driver?.availability !== 'online' && (
            <button
              onClick={() => handleStatusChange('online')}
              disabled={statusUpdating}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '10px',
                backgroundColor: '#E8F5E9',
                color: '#2E7D32',
                border: '1px solid #C8E6C9',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: statusUpdating ? 'not-allowed' : 'pointer',
              }}
            >
              Go Online
            </button>
          )}
          {driver?.availability !== 'busy' && driver?.availability === 'online' && (
            <button
              onClick={() => handleStatusChange('busy')}
              disabled={statusUpdating}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '10px',
                backgroundColor: '#FFF3E0',
                color: '#E65100',
                border: '1px solid #FFE0B2',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: statusUpdating ? 'not-allowed' : 'pointer',
              }}
            >
              Go Busy
            </button>
          )}
          {driver?.availability !== 'offline' && (
            <button
              onClick={() => handleStatusChange('offline')}
              disabled={statusUpdating}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '10px',
                backgroundColor: '#FFEAEA',
                color: '#C62828',
                border: '1px solid #FFCDCD',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: statusUpdating ? 'not-allowed' : 'pointer',
              }}
            >
              Go Offline
            </button>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '12px',
        marginBottom: '24px',
      }}>
        <Link
          to="/driver/orders"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px',
            backgroundColor: '#D4AF37',
            color: '#fff',
            borderRadius: '12px',
            textDecoration: 'none',
            fontWeight: 600,
            fontSize: '0.95rem',
            justifyContent: 'center',
          }}
        >
          <ClipboardList size={20} />
          View Orders
        </Link>
        <Link
          to="/driver/history"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px',
            backgroundColor: '#1a1a1a',
            color: '#fff',
            borderRadius: '12px',
            textDecoration: 'none',
            fontWeight: 600,
            fontSize: '0.95rem',
            justifyContent: 'center',
          }}
        >
          <Clock size={20} />
          History
        </Link>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '12px',
        marginBottom: '24px',
      }}>
        {statCards.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div
              key={i}
              style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid #EAEDF1',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px',
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  backgroundColor: stat.color + '20',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Icon size={16} color={stat.color} />
                </div>
                <span style={{ fontSize: '0.8rem', color: '#666' }}>{stat.label}</span>
              </div>
              <p style={{
                fontSize: '1.4rem',
                fontWeight: 700,
                color: '#1A1A1A',
                margin: 0,
              }}>
                {stat.value}
              </p>
            </div>
          );
        })}
      </div>

      {/* Rating Card */}
      {stats.rating > 0 && (
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '16px',
          border: '1px solid #EAEDF1',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: '#FFF8E1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Star size={24} color="#FFC107" />
          </div>
          <div>
            <p style={{ fontSize: '0.85rem', color: '#666', margin: 0 }}>Your Rating</p>
            <p style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1A1A1A', margin: 0 }}>
              {stats.rating.toFixed(1)} / 5.0
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverDashboard;
